import { openDB } from "idb";
import { DEFAULT_MEMO_LIMIT } from "@/helpers/consts";

const DB_NAME = "cflow-ui-cache";
const DB_VERSION = 1;
const STORE_NAME = "memo-feed";
const CACHE_VERSION = 1;
const MAX_CACHE_AGE = 7 * 24 * 60 * 60 * 1000;

interface MemoFeedCacheRecord {
  username: string;
  version: number;
  savedAt: number;
  memos: Memo[];
}

const getDatabase = () =>
  openDB(DB_NAME, DB_VERSION, {
    upgrade(database) {
      if (!database.objectStoreNames.contains(STORE_NAME)) {
        database.createObjectStore(STORE_NAME, { keyPath: "username" });
      }
    },
  });

export const readMemoFeedCache = async (username: string): Promise<Memo[]> => {
  if (!username) return [];

  try {
    const database = await getDatabase();
    const record = (await database.get(STORE_NAME, username)) as MemoFeedCacheRecord | undefined;
    if (!record || record.version !== CACHE_VERSION || Date.now() - record.savedAt > MAX_CACHE_AGE) {
      return [];
    }
    return Array.isArray(record.memos) ? record.memos : [];
  } catch (error) {
    console.warn("读取首页卡片缓存失败，将继续从服务器加载", error);
    return [];
  }
};

export const writeMemoFeedCache = async (username: string, memos: Memo[]): Promise<void> => {
  if (!username) return;

  const cachedMemos = memos
    .filter((memo) => memo.creatorUsername === username && memo.rowStatus === "NORMAL")
    .sort((left, right) => right.displayTs - left.displayTs)
    .slice(0, DEFAULT_MEMO_LIMIT);

  try {
    const database = await getDatabase();
    const record: MemoFeedCacheRecord = {
      username,
      version: CACHE_VERSION,
      savedAt: Date.now(),
      memos: cachedMemos,
    };
    await database.put(STORE_NAME, record);
  } catch (error) {
    console.warn("写入首页卡片缓存失败", error);
  }
};

export const clearMemoFeedCache = async (username?: string): Promise<void> => {
  try {
    const database = await getDatabase();
    if (username) {
      await database.delete(STORE_NAME, username);
    } else {
      await database.clear(STORE_NAME);
    }
  } catch (error) {
    console.warn("清理首页卡片缓存失败", error);
  }
};
